import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    addLead,
    updateLead,
    getLeadById
} from '../services/leadService';   // ✔ FIXED — correct imports

import { getUserById } from '../services/userServices';
import { sendEmail, sendWhatsApp } from '../services/spEmailServices';
import { toInputDateValue, formatTime12Hour } from '../utils/dateFormatter';

const toDateOnly = (value) => {
    if (!value) return '';
    return toInputDateValue(value);
};

const toTimeOnly = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    const fromString = raw.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (fromString) {
        const h = Number(fromString[1]);
        const m = Number(fromString[2]);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return formatTime12Hour(d);
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '';

    return formatTime12Hour(parsed);
};

const sanitizeLeadDates = (lead) => {
    const source = String(lead?.source || '').toLowerCase();
    const isWebsiteLead = source.includes('website');
    const hasEventDate = Boolean(lead?.event_date);
    const hasFollowUpDate = Boolean(lead?.follow_up_date);

    if (!isWebsiteLead || !hasEventDate || !hasFollowUpDate) {
        return lead;
    }

    const sameDate = toDateOnly(lead.follow_up_date) === toDateOnly(lead.event_date);

    if (!sameDate) {
        return lead;
    }

    return {
        ...lead,
        follow_up_date: ''
    };
};


const useLeadForm = (initialLeadData, isEdit = false, leadId = null) => {
    const navigate = useNavigate();

    // main form data
    const [leadData, setLeadData] = useState(initialLeadData);

    // [{ field_id, field_value }]
    const [customFields, setCustomFields] = useState([]);

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const [activeTab, setActiveTab] = useState('leadDetails');


    // ─────────────────────────────────────────────
    // LOAD LEAD DATA ON EDIT
    // ─────────────────────────────────────────────
    useEffect(() => {
        if (!isEdit || !leadId) return;

        const loadLeadData = async () => {
            try {
                const lead = sanitizeLeadDates(await getLeadById(leadId));

                // set main fields
                setLeadData(prev => ({
                    ...prev,
                    ...lead,
                    event_date: toDateOnly(lead.event_date),
                    event_time: toTimeOnly(lead.event_time),
                    event_start_date: toDateOnly(lead.event_start_date),
                    event_start_time: toTimeOnly(lead.event_start_time),
                    event_end_date: toDateOnly(lead.event_end_date),
                    event_end_time: toTimeOnly(lead.event_end_time),
                    custom_fields: lead.custom_fields || []
                }));

                // convert custom fields from db → correct frontend format
                if (lead.custom_fields?.length) {
                    setCustomFields(
                        lead.custom_fields.map(cf => ({
                            field_id: cf.field_id,
                            field_value: cf.field_value || ""
                        }))
                    );
                } else {
                    setCustomFields([]);
                }

            } catch (err) {
                console.error("❌ Error loading lead:", err);
            }
        };

        loadLeadData();
    }, [isEdit, leadId]);


    // ─────────────────────────────────────────────
    // GENERAL FIELD CHANGE HANDLER
    // ─────────────────────────────────────────────
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;

        setLeadData(prev => ({
            ...prev,
            [name]: value,
        }));
    }, []);


    // ─────────────────────────────────────────────
    // CUSTOM FIELD HANDLER (ALREADY IN CORRECT FORMAT)
    // ─────────────────────────────────────────────
    const handleCustomFieldsUpdate = useCallback((fields) => {
        setCustomFields(fields);
    }, []);


    // ─────────────────────────────────────────────
    // SEND EMAIL TO ASSIGNED SALESPERSON
    // ─────────────────────────────────────────────
    const sendEmailtoSp = async () => {
        try {
            const sp = await getUserById(leadData.assigned_salesperson);

            if (!sp?.email) {
                throw new Error("Assigned salesperson has no email.");
            }

            const payload = {
                ...leadData,
                custom_fields: customFields,
                salesperson_email: sp.email
            };

            await sendEmail(payload);

            setNotification({
                open: true,
                message: 'Email sent successfully!',
                severity: 'success',
            });

        } catch (err) {
            console.error("❌ Email sending failed:", err);
            setNotification({
                open: true,
                message: err.message || "Failed to send email.",
                severity: "error",
            });
        }
    };

    const sendWhatsApptoSp = async () => {
        try {
            const sp = await getUserById(leadData.assigned_salesperson);

            if (!sp?.phone_number) {
                throw new Error("Assigned salesperson has no phone number.");
            }

            const payload = {
                ...leadData,
                custom_fields: customFields,
                salesperson_phone_number: sp.phone_number
            };

            await sendWhatsApp(payload);

            setNotification({
                open: true,
                message: 'WhatsApp sent successfully!',
                severity: 'success',
            });

        } catch (err) {
            console.error("❌ WhatsApp sending failed:", err);
            setNotification({
                open: true,
                message: err.message || "Failed to send WhatsApp.",
                severity: "error",
            });
        }
    };


    // ─────────────────────────────────────────────
    // CREATE / UPDATE LEAD
    // ─────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                ...leadData,
                custom_fields: customFields  // ALWAYS correct format
            };

            if (isEdit) {
                await updateLead(leadId, payload);

                setNotification({
                    open: true,
                    message: 'Lead updated successfully!',
                    severity: 'success',
                });

            } else {
                await addLead(payload);

                setNotification({
                    open: true,
                    message: 'Lead added successfully!',
                    severity: 'success',
                });
            }

            navigate('/leads');

        } catch (err) {
            console.error("❌ Error saving lead:", err);
            setNotification({
                open: true,
                message:
                    err.message ||
                    (isEdit ? "Failed to update lead." : "Failed to add lead."),
                severity: "error",
            });
        }
    };


    // ─────────────────────────────────────────────
    // CLOSE NOTIFICATION
    // ─────────────────────────────────────────────
    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };


    return {
        leadData,
        setLeadData,

        customFields,
        setCustomFields,
        handleCustomFieldsUpdate,

        notification,
        handleCloseNotification,

        activeTab,
        setActiveTab,

        handleChange,
        handleSubmit,
        sendEmailtoSp,
        sendWhatsApptoSp
    };
};

export default useLeadForm;
